
import React, { useRef, useCallback,useMemo, useState } from "react";
import Select, { components, SingleValue } from "react-select";
import { useModel, useEmptyClearFilterModel } from "../../contexts/modelContextNew";
import BaseField from "./baseFieldNew";
import { Option } from "../../../models/Option"
import { IPropsSelectField } from "./fieldPropertiesInterfaceNew";
import modelHelper from "./modelHelperNew";
import "./selectFieldNew.scss";
import { ReactComponent as CloseIcon } from "itd-common/assets/icons/close.svg";

function SelectField({
  options,
  model,
  attr,
  value,
  label,
  onChange,
  disabled,
  required,
  className,
  notAll,
  defaultOption,
  menuPosition = 'absolute'
}: IPropsSelectField) {
  const modelContext = useModel();
  const emptyClearStates = useEmptyClearFilterModel();
  const modelState = model || modelContext;

  const { onValueChange, getTextValue, singleClearState } = modelHelper({
    model: modelState,
    attr,
    value,
    onChange,
    emptyClearStates
  });
  const defaultOptionValue= defaultOption ?? "Seleccionar";
  const currentValue = getTextValue() || "";
  const selectRef = useRef<any>(null);
  // Controlar apertura del menú para abrirlo al click del ícono
  const [menuIsOpen, setMenuIsOpen] = useState<boolean | undefined>(undefined);
  
   const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 999999 }),  
    menu: (base: any) => ({ ...base, zIndex: 999999 }),  
    indicatorsContainer: (base: any) => ({
      ...base,
      flexDirection: 'row-reverse',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: 'black', 
      svg: { fill: 'black' }
    }),
    clearIndicator: (base: any) => ({
      ...base,
      color: 'black', 
      svg: { fill: 'black' }
    }),
    container: (base: any) => ({
      ...base,
      width: '100%', 
    }),
    control: (base: any, state: any) => ({
        ...base,
        width: "100%",
        minWidth: 0,           
        borderWidth: 1.5,               
        borderStyle: 'solid',
        boxShadow: 'none',              
        borderRadius: 12,
        borderColor: '#CECECE',
        // Si tiene valor 
        ...(currentValue !== "" && {
          borderColor: 'black', 
          borderWidth: 1.7,
        }),
        ...(state.isFocused && {
          borderColor: 'black',
          borderWidth: 1.7,
        }),
        "&:hover": {
          borderColor: 'black',
          borderWidth: 1.7,
        }
    }), 
    placeholder: (base: any) => ({
      ...base,
      color: currentValue !== "" ? 'black': '#9D9B9B',
    }),

  };

  // Objeto seleccionado para react-select
  const selectedOption = useMemo<Option | null>(() => {
    const optionsArray = Array.isArray(options) ? (options as Option[]) : [];
    const found = optionsArray.find(o => o.value === currentValue);
    return found || null;
  }, [options, currentValue]);

  // Placeholder “Ver todos” cuando !notAll
  const placeholder = !notAll ? defaultOptionValue : undefined;

  // Emular evento { target: { value } } para reutilizar onValueChange del modelo
  const handleChange = (opt: SingleValue<Option>) => {
    const nextValue = opt?.value ?? ""; // si limpiás: ""

  onValueChange({ target: { value: nextValue } } as any); // <-- evita TS2345
    // cerrar menú si es single-select (comportamiento nativo)
    setMenuIsOpen(undefined);
  };

  // Abrir el menú al click del ícono (y focus)
  const handleIconClick = useCallback(() => {
    if (disabled) return;
    selectRef.current?.focus();
    setMenuIsOpen(true);
  }, [disabled]);

  
  const ClearIndicator = (props: any) => {
    const { innerProps } = props;
  
    return (
      <components.ClearIndicator {...props}>
        {/* se mantiene innerProps para click/hover/aria */}
        <div {...innerProps} style={{ display: 'flex', alignItems: 'center' }}>
          <CloseIcon color='black' height='18' width='18'/>
        </div>
      </components.ClearIndicator>
    );
  };


  // Cerrar cuando el control pierde foco o se cierra el menú
  const handleMenuClose = () => setMenuIsOpen(undefined);

  return (
    
    <BaseField
      attr={attr}
      label={label ?? ""}
      required={required}
      value={currentValue}
      className={className}
      disabledBorder={true}
      hideSingleClearState={true}
    >
      <Select<Option, false>
        ref={selectRef}
        inputId={`input-${attr || label}`}
        className="custom-select-new"
        classNamePrefix="custom-select"      
        isDisabled={!!disabled}
        isClearable={true}                  
        isSearchable={false}            
        menuPortalTarget={document?.body}
        menuPlacement="bottom"
        menuPosition="absolute"                 
        placeholder={placeholder}
        options={options as Option[]}
        value={selectedOption}
        onChange={handleChange}
        components={{ ClearIndicator }} 
        menuIsOpen={menuIsOpen}
        onMenuClose={handleMenuClose}
        aria-label={typeof label === "string" ? label : "Seleccionar opción"}
        aria-required={!!required}
        styles={customStyles}      
        noOptionsMessage={() => 'No hay opciones'}
      />
    </BaseField>
  );
}

export default SelectField;